import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  VStack,
  Text,
  Container,
  HStack,
  Badge,
  useToast,
  FormControl,
  FormLabel,
  Flex,
  InputGroup,
  InputRightElement
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './Reports.css';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiRefreshCw , FiSearch} from 'react-icons/fi';
import PageHeader from './shared/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { getUserReports } from '../utils/firebaseUtils';
import type { Report } from '../utils/firebaseUtils';
import CustomDropdown from './subComponents/DropDown'
import type { DropdownOption } from './subComponents/DropDown';

const Reports: React.FC = () => {
  const [filteredReports, setFilteredReports] = useState<Report[]>([]);
  // const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Set to end of today to include full day
  const now = new Date(); // Current moment for filtering future dates
  now.setHours(23, 59, 59, 999); // Set to end of current day to allow today selection
  const offsetDaysAgo = new Date();
  offsetDaysAgo.setDate(today.getDate() - 5);
  
  // Create date boundaries for the last 6 months
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(today.getMonth() - 6);
  
  const [dateFrom, setDateFrom] = useState<Date | null>(offsetDaysAgo);
  const [dateTo, setDateTo] = useState<Date | null>(today);
  const [reportType, setReportType] = useState('all');
  const [hasSearched, setHasSearched] = useState(false);
  
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedCompany, reportTypes } = useAuth();

  const handleSearch = async () => {
    // Validate search criteria
    if (!dateFrom || !dateTo) {
      toast({
        title: 'Missing Date Range',
        description: 'Please select both start and end dates',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (dateFrom > dateTo) {
      toast({
        title: 'Invalid Date Range',
        description: 'Start date cannot be later than end date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedCompany?.companyName) {
      toast({
        title: 'Missing Company',
        description: 'User company information is required for search',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // console.log({selectedCompany})
    setSearchLoading(true);
    setHasSearched(true);
    try {
      const userReports = await getUserReports(selectedCompany);
      console.log('🔍 Search: Got reports from Firestore:', userReports.length);
      
      let filtered = userReports;

      // Filter by report type (only if not 'all')
      if (reportType !== 'all') {
        const initialCount = filtered.length;
        filtered = filtered.filter(report => {
          // Check both reportType and type fields for compatibility
          const matches = (report.reportType === reportType) || (report.type === reportType);
          if (!matches) {
            console.log(`🚫 Filtered out report due to type: ${report.reportType || report.type} !== ${reportType}`);
          }
          return matches;
        });
        console.log(`📊 After type filter: ${filtered.length}/${initialCount} reports`);
      }

      // Filter by date range
      const initialCount = filtered.length;
      filtered = filtered.filter(report => {
        let reportDate: Date;
        
        // Check if report has a reportDate field first
        if (report.reportDate) {
          if (report.reportDate.toDate) {
            reportDate = report.reportDate.toDate();
          } else if (report.reportDate instanceof Date) {
            reportDate = report.reportDate;
          } else {
            reportDate = new Date(report.reportDate as unknown as string);
          }
        } else {
          // Fallback to createdAt
          if (report.createdAt?.toDate) {
            reportDate = report.createdAt.toDate();
          } else if (report.createdAt instanceof Date) {
            reportDate = report.createdAt;
          } else {
            reportDate = new Date(report.createdAt as unknown as string);
          }
        }
        
        // Create date range that includes the entire end day
        const startOfDay = new Date(dateFrom);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        
        // Extract just the date part for comparison (ignore time)
        const reportDateOnly = new Date(reportDate);
        reportDateOnly.setHours(0, 0, 0, 0);
        
        const dateFromOnly = new Date(dateFrom);
        dateFromOnly.setHours(0, 0, 0, 0);
        
        const dateToOnly = new Date(dateTo);
        dateToOnly.setHours(0, 0, 0, 0);
        
        const isInRange = reportDateOnly >= dateFromOnly && reportDateOnly <= dateToOnly;
        
        if (!isInRange) {
          console.log(`🚫 Filtered out report due to date: ${reportDate.toDateString()} not in range ${dateFrom.toDateString()} - ${dateTo.toDateString()}`);
          console.log(`   - Report date (date only): ${reportDateOnly.toDateString()}`);
          console.log(`   - Range: ${dateFromOnly.toDateString()} to ${dateToOnly.toDateString()}`);
        } else {
          console.log(`✅ Report date ${reportDate.toDateString()} is in range ${dateFrom.toDateString()} - ${dateTo.toDateString()}`);
        }
        
        return isInRange;
      });
      
      console.log(`📅 After date filter: ${filtered.length}/${initialCount} reports`);
      console.log('✅ Final filtered reports:', filtered.map(r => ({ id: r.id, fileName: r.fileName, date: r.reportDate || r.createdAt })));
      
      setFilteredReports(filtered);
      
      toast({
        title: 'Search Complete',
        description: `Found ${filtered.length} report(s) matching your criteria`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error during search:', error);
      toast({
        title: 'Search Error',
        description: error instanceof Error ? error.message : 'Failed to search reports',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClearFilters = () => {
    setReportType('all');
    setDateFrom(null);
    setDateTo(null);
    setFilteredReports([]);
    setHasSearched(false);
  };

  // Check if search criteria are valid
  const isSearchValid = () => {
    if (!dateFrom || !dateTo) return false;
    if (dateFrom > dateTo) return false;
    if (!selectedCompany?.companyName) return false;
    return true;
  };

  // Get validation message for search requirements
  const getValidationMessage = () => {
    if (!selectedCompany?.companyName) {
      return "You have not been assigned to a company yet. Please contact your administrator to assign you to a company before searching reports.";
    }
    if (!dateFrom || !dateTo) {
      return "Please select both start and end dates to search reports";
    }
    if (dateFrom > dateTo) {
      return "Start date must be before or equal to end date";
    }
    return "";
  };

  const formatDate = (timestamp: unknown) => {
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      return new Date((timestamp as { seconds: number }).seconds * 1000).toLocaleDateString();
    }
    return new Date(timestamp as string | number | Date).toLocaleDateString();
  };

  const getReportTypeBadgeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'sales': return 'green';
      case 'financial': return 'blue';
      case 'feedback': return 'purple';
      case 'analytics': return 'orange';
      default: return 'gray';
    }
  };

  const reportTypeOptions: DropdownOption[] = Array.from(reportTypes.entries())
    .sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0))
    .map(([key, type]) => ({
      id: String(type.value || key),
      label: type.name,
      iconSrc: type.icon,
      subtitle: type.description,
    }));

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)">
      <PageHeader
        title="Reports"
        subtitle="View and export company reports"
        icon="📊"
        onBack={() => navigate('/dashboard')}
      />

  <Container maxW="6xl" px={{ base: 4, md: 6 }} py={12} w="100%">
        <VStack spacing={{ base: 8, md: 10 }} w="100%">
          {/* Filters */}
          <Box
            w="100%" 
            maxW="4xl"
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
          >
            <VStack spacing={6} w="100%">
              <VStack spacing={4} textAlign="center">
                <Box
                  p={3}
                  borderRadius="xl"
                  bgGradient="linear(135deg, blue.400, purple.500)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 6px 24px rgba(59, 130, 246, 0.3)"
                >
                  <Text fontSize="2xl">🔍</Text>
                </Box>
                <VStack spacing={2}>
                  <Text 
                    fontSize="xl" 
                    fontWeight="700"
                    bgGradient="linear(135deg, gray.800, gray.600)"
                    bgClip="text"
                  >
                    Search Reports
                  </Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="500">
                    Filter your reports by type and date range
                  </Text>
                </VStack>
              </VStack>

              <VStack spacing={6} w="100%">
                <VStack spacing={6} w="100%">
                  {/* Report Type Filter */}
                  <FormControl>
                    <FormLabel 
                      fontSize="md" 
                      fontWeight="700" 
                      color="gray.700"
                      mb={3}
                    >
                      📋 Report Type
                    </FormLabel>
                    <CustomDropdown
                      options={reportTypeOptions}
                      selectedId={reportType}
                      onSelectionChange={setReportType}
                      placeholder="Select Report Type"
                      showIcons={true}
                      showBadgeForSingle={true}
                      maxWidth={{ base: "100%", md: "220px" }}
                    />
                  </FormControl>

                  {/* Date Range */}
                  <VStack spacing={4} w="100%">
                    <FormLabel 
                      fontSize="md" 
                      fontWeight="700" 
                      color="gray.700"
                      alignSelf="flex-start"
                      mb={1}
                    >
                      📅 Date Range
                    </FormLabel>
                    <HStack spacing={4} w="100%" flexDirection={{ base: "column", md: "row" }}>
                      <FormControl flex={1}>
                        <FormLabel fontSize="sm" fontWeight="600" color="gray.600">From Date</FormLabel>
                        <InputGroup className="reports-date-input-group" w="100%" position="relative">
                          <div className="reports-datepicker-wrapper" style={{ width: '100%' }}>
                            <DatePicker
                              selected={dateFrom}
                              onChange={(date) => setDateFrom(date)}
                              placeholderText="Select start date"
                              className="chakra-input custom-datepicker"
                              dateFormat="yyyy-MM-dd"
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              minDate={sixMonthsAgo}
                              maxDate={dateTo || today}
                              filterDate={(date) => date <= now}
                              withPortal
                              portalId="reports-datepicker-portal"
                              showPopperArrow={false}
                              renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => (
                                <div className="react-datepicker__header">
                                  <button
                                    type="button"
                                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                                    onClick={decreaseMonth}
                                    disabled={prevMonthButtonDisabled}
                                    style={{
                                      position: 'absolute',
                                      left: '12px',
                                      top: '18px',
                                      width: '32px',
                                      height: '32px',
                                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                      border: '2px solid #2d3748',
                                      borderRadius: '50%',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      color: '#2d3748',
                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                      zIndex: 9999999999999
                                    }}
                                  >
                                    <FiChevronLeft size={20} />
                                  </button>
                                  
                                  <div className="react-datepicker__current-month">
                                    {date.toLocaleDateString('en-US', {
                                      month: 'long',
                                      year: 'numeric',
                                    })}
                                  </div>
                                  
                                  <button
                                    type="button"
                                    className="react-datepicker__navigation react-datepicker__navigation--next"
                                    onClick={increaseMonth}
                                    disabled={nextMonthButtonDisabled}
                                    style={{
                                      position: 'absolute',
                                      right: '12px',
                                      top: '18px',
                                      width: '32px',
                                      height: '32px',
                                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                      border: '2px solid #2d3748',
                                      borderRadius: '50%',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      color: '#2d3748',
                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                      zIndex: 9999999999999
                                    }}
                                  >
                                    <FiChevronRight size={20} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                          <InputRightElement pointerEvents="none">
                            <FiCalendar color="#718096" />
                          </InputRightElement>
                        </InputGroup>
                      </FormControl>

                      <FormControl flex={1}>
                        <FormLabel fontSize="sm" fontWeight="600" color="gray.600">To Date</FormLabel>
                        <InputGroup className="reports-date-input-group" w="100%" position="relative">
                          <div className="reports-datepicker-wrapper" style={{ width: '100%' }}>
                            <DatePicker
                              selected={dateTo}
                              onChange={(date) => setDateTo(date)}
                              placeholderText="Select end date"
                              className="chakra-input custom-datepicker"
                              dateFormat="yyyy-MM-dd"
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              minDate={sixMonthsAgo}
                              maxDate={today}
                              filterDate={(date) => date <= now}
                              withPortal
                              portalId="reports-datepicker-portal"
                              showPopperArrow={false}
                              renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => (
                                <div className="react-datepicker__header">
                                  <button
                                    type="button"
                                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                                    onClick={decreaseMonth}
                                    disabled={prevMonthButtonDisabled}
                                    style={{
                                      position: 'absolute',
                                      left: '12px',
                                      top: '18px',
                                      width: '32px',
                                      height: '32px',
                                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                      border: '2px solid #2d3748',
                                      borderRadius: '50%',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      color: '#2d3748',
                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                      zIndex: 9999999999999
                                    }}
                                  >
                                    <FiChevronLeft size={20} />
                                  </button>
                                  
                                  <div className="react-datepicker__current-month">
                                    {date.toLocaleDateString('en-US', {
                                      month: 'long',
                                      year: 'numeric',
                                    })}
                                  </div>
                                  
                                  <button
                                    type="button"
                                    className="react-datepicker__navigation react-datepicker__navigation--next"
                                    onClick={increaseMonth}
                                    disabled={nextMonthButtonDisabled}
                                    style={{
                                      position: 'absolute',
                                      right: '12px',
                                      top: '18px',
                                      width: '32px',
                                      height: '32px',
                                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                      border: '2px solid #2d3748',
                                      borderRadius: '50%',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      color: '#2d3748',
                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                      zIndex: 9999999999999
                                    }}
                                  >
                                    <FiChevronRight size={20} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                          <InputRightElement pointerEvents="none">
                            <FiCalendar color="#718096" />
                          </InputRightElement>
                        </InputGroup>
                      </FormControl>
                    </HStack>
                  </VStack>

                  {/* Search Requirements Info */}
                  {!isSearchValid() && (
                    <Box
                      bg="orange.50"
                      border="1px solid"
                      borderColor="orange.200"
                      borderRadius="xl"
                      p={4}
                      w="100%"
                    >
                      <HStack spacing={3}>
                        <Text fontSize="lg">⚠️</Text>
                        <Text fontSize="sm" color="orange.700" fontWeight="500">
                          {getValidationMessage()}
                        </Text>
                      </HStack>
                    </Box>
                  )}

                  {/* Search and Clear Buttons */}
                  <Flex gap={4} justify="center" pt={4}>
                                    <Button
                                      flex={{ base: "unset", md: 1 }}
                                      leftIcon={<FiSearch />}
                                      bgGradient="linear(135deg, blue.500, purple.600)"
                                      color="white"
                                      size="lg"
                                      onClick={handleSearch}
                                      isLoading={searchLoading}
                                      loadingText="Searching..."
                                      isDisabled={!isSearchValid}
                                      px={8}
                                      py={3}
                                      borderRadius="xl"
                                      _hover={{
                                        bgGradient: "linear(135deg, blue.600, purple.700)",
                                        transform: "translateY(-2px)",
                                        boxShadow: "xl"
                                      }}
                                      _active={{
                                        transform: "translateY(0)"
                                      }}
                                      transition="all 0.2s"
                                      fontWeight="700"
                                      boxShadow="lg"
                                    >
                                      Search
                                    </Button>
                                    <Button
                                      flex={{ base: "unset", md: 1 }}
                                      leftIcon={<FiRefreshCw />}
                                      variant="outline"
                                      borderColor="gray.300"
                                      color="gray.700"
                                      size="lg"
                                      onClick={handleClearFilters}
                                      px={8}
                                      py={3}
                                      borderRadius="xl"
                                      _hover={{
                                        bg: "gray.50",
                                        transform: "translateY(-2px)",
                                        boxShadow: "lg"
                                      }}
                                      _active={{
                                        transform: "translateY(0)"
                                      }}
                                      transition="all 0.2s"
                                      fontWeight="600"
                                      borderWidth="2px"
                                    >
                                      Reset
                                    </Button>
                                  </Flex>

                  
                </VStack>
              </VStack>
            </VStack>
          </Box>

          {/* Reports List */}
          <Box
            w="100%" 
            maxW="4xl"
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            boxShadow="0 8px 32px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="whiteAlpha.500"
            overflow="hidden"
          >
            {!hasSearched ? (
              <Box p={{ base: 8, md: 12 }} textAlign="center" w="100%">
                <VStack spacing={6}>
                  <Box
                    p={4}
                    borderRadius="full"
                    bgGradient="linear(135deg, gray.400, gray.600)"
                    w="80px"
                    h="80px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mx="auto"
                    boxShadow="0 8px 32px rgba(107, 114, 128, 0.3)"
                  >
                    <Text fontSize="3xl">📄</Text>
                  </Box>
                  <VStack spacing={3}>
                    <Text 
                      fontSize="xl" 
                      fontWeight="700"
                      bgGradient="linear(135deg, gray.800, gray.600)"
                      bgClip="text"
                    >
                      Ready to Search
                    </Text>
                    <Text fontSize="md" color="gray.600" maxW="2xl" mx="auto" fontWeight="500">
                      Please use the filters above and click <strong>Search Reports</strong> to view your reports.
                    </Text>
                  </VStack>
                </VStack>
              </Box>
            ) : filteredReports.length === 0 ? (
              <Box p={{ base: 8, md: 12 }} textAlign="center" w="100%">
                <VStack spacing={6}>
                  <Box
                    p={4}
                    borderRadius="full"
                    bgGradient="linear(135deg, orange.400, orange.600)"
                    w="80px"
                    h="80px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mx="auto"
                    boxShadow="0 8px 32px rgba(251, 146, 60, 0.3)"
                  >
                    <Text fontSize="3xl">🔍</Text>
                  </Box>
                  <VStack spacing={3}>
                    <Text 
                      fontSize="xl" 
                      fontWeight="700"
                      bgGradient="linear(135deg, gray.800, gray.600)"
                      bgClip="text"
                    >
                      No Results Found
                    </Text>
                    <Text fontSize="md" color="gray.600" maxW="2xl" mx="auto" fontWeight="500">
                      No reports match your filters. Try adjusting your search criteria.
                    </Text>
                  </VStack>
                </VStack>
              </Box>
            ) : (
              <Box w="100%">
                {/* Search Results Count */}
                <Box 
                  p={{ base: 6, md: 8 }} 
                  pb={{ base: 4, md: 6 }} 
                  borderBottom="1px" 
                  borderColor="whiteAlpha.300"
                  bg="rgba(59, 130, 246, 0.05)"
                >
                  <HStack spacing={3}>
                    <Box
                      p={2}
                      borderRadius="lg"
                      bgGradient="linear(135deg, green.400, green.600)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="sm" color="white">✅</Text>
                    </Box>
                    <Text 
                      fontSize="md" 
                      fontWeight="700"
                      bgGradient="linear(135deg, green.600, green.800)"
                      bgClip="text"
                    >
                      Search Results ({filteredReports.length} records)
                    </Text>
                  </HStack>
                </Box>
                
                <VStack spacing={6} p={{ base: 6, md: 8 }} w="100%">
                  {filteredReports.map((report) => (
                    <Box
                      key={report.id}
                      w="100%"
                      bg="rgba(255, 255, 255, 0.8)"
                      backdropFilter="blur(10px)"
                      borderRadius="xl"
                      p={{ base: 6, md: 8 }}
                      boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                      border="1px solid"
                      borderColor="whiteAlpha.300"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)"
                      }}
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                      <VStack spacing={5} align="stretch" w="100%">
                        {/* Report Header */}
                        <HStack spacing={4}>
                          <Box
                            p={3}
                            borderRadius="lg"
                            bgGradient="linear(135deg, blue.400, purple.500)"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            boxShadow="0 4px 16px rgba(59, 130, 246, 0.3)"
                          >
                            <Text fontSize="lg" color="white">📄</Text>
                          </Box>
                          <VStack align="start" spacing={1} flex={1}>
                            <Text 
                              fontWeight="700" 
                              fontSize={{ base: "md", md: "lg" }} 
                              bgGradient="linear(135deg, gray.800, gray.600)"
                              bgClip="text"
                              noOfLines={2}
                            >
                              {report.fileName || report.title}
                            </Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">
                              Generated Report
                            </Text>
                          </VStack>
                        </HStack>

                        {/* Report Details */}
                        <VStack spacing={3} align="stretch">
                          <Box
                            bg="rgba(59, 130, 246, 0.05)"
                            borderRadius="lg"
                            p={4}
                            border="1px solid"
                            borderColor="blue.100"
                          >
                            <HStack justify="space-between" flexWrap="wrap">
                              <HStack spacing={2}>
                                <Text fontSize="lg">📅</Text>
                                <Text fontSize="sm" color="gray.700" fontWeight="600">Report Date:</Text>
                              </HStack>
                              <Text fontSize="sm" color="blue.600" fontWeight="700">
                                {report.reportDate ? formatDate(report.reportDate) : 'N/A'}
                              </Text>
                            </HStack>
                          </Box>

                          <Box
                            bg="rgba(168, 85, 247, 0.05)"
                            borderRadius="lg"
                            p={4}
                            border="1px solid"
                            borderColor="purple.100"
                          >
                            <HStack justify="space-between" flexWrap="wrap">
                              <HStack spacing={2}>
                                <Text fontSize="lg">📊</Text>
                                <Text fontSize="sm" color="gray.700" fontWeight="600">Report Type:</Text>
                              </HStack>
                              <Badge 
                                colorScheme={getReportTypeBadgeColor(report.reportType || report.type || 'default')}
                                textTransform="capitalize"
                                borderRadius="lg"
                                px={3}
                                py={1}
                                fontWeight="600"
                                fontSize="xs"
                              >
                                {report.reportTypeName || report.reportType || report.type || 'Unknown'}
                              </Badge>
                            </HStack>
                          </Box>
                        </VStack>

                        {/* Download Button */}
                        <Button
                          size={{ base: "md", md: "lg" }}
                          bgGradient="linear(135deg, green.400, green.600)"
                          color="white"
                          onClick={() => window.open(report.fileUrl, '_blank')}
                          isDisabled={!report.fileUrl}
                          w="100%"
                          leftIcon={<span>�</span>}
                          borderRadius="xl"
                          boxShadow="0 4px 20px rgba(34, 197, 94, 0.4)"
                          _hover={{
                            transform: "translateY(-1px)",
                            boxShadow: "0 6px 24px rgba(34, 197, 94, 0.5)"
                          }}
                          _active={{ transform: "translateY(0px)" }}
                          transition="all 0.2s"
                          fontWeight="600"
                          py={6}
                        >
                          Download Report
                        </Button>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Reports;
